// src/components/store/sections/branch/BranchStoresSection.tsx
"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { 
  Building2, 
  MapPin, 
  Phone, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  ArrowRight,
  PackageCheck
} from "lucide-react";

export interface BranchItem {
  id: number | string;
  name: string;
  slug?: string;
  domain?: string;
  subdomain?: string;
  logo?: string | null;
  logo_url?: string | null;
  banner?: string | null;
  banner_url?: string | null;
  image?: string | null;
  address?: string;
  phone?: string;
  contact_phone?: string;
  total_products?: number;
  products_count?: number;
  is_active?: boolean;
}

interface BranchStoresSectionProps {
  subdomain?: string;
  initialBranches?: BranchItem[];
  primaryColor?: string;
}

const BRANCH_CARD_THEMES = [
  {
    gradient: "from-blue-600 to-indigo-800",
    waveFill: "#3b82f6",
    tagText: "VERIFIED BRANCH",
    badge: "PHYSICAL OUTLET",
    btnBorder: "border-blue-200",
  },
  {
    gradient: "from-emerald-600 to-teal-800",
    waveFill: "#10b981",
    tagText: "EXCLUSIVE OUTLET",
    badge: "FULL STOCK",
    btnBorder: "border-emerald-200",
  },
  {
    gradient: "from-amber-500 to-orange-700",
    waveFill: "#f97316",
    tagText: "EXPRESS BRANCH",
    badge: "QUICK PICKUP",
    btnBorder: "border-orange-200",
  },
  {
    gradient: "from-purple-600 to-fuchsia-800",
    waveFill: "#a855f7",
    tagText: "FLAGSHIP STORE",
    badge: "PREMIUM OUTLET",
    btnBorder: "border-purple-200",
  },
];

export default function BranchStoresSection({
  subdomain = "nextlook",
  initialBranches = [],
  primaryColor = "#0284c7",
}: BranchStoresSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [branches, setBranches] = useState<BranchItem[]>(initialBranches);
  const [loading, setLoading] = useState<boolean>(initialBranches.length === 0);

  const getSubdomain = useCallback(() => {
    if (subdomain && subdomain.trim()) return subdomain.trim();

    if (typeof window !== "undefined") {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes(".")) {
        const parts = hostname.split(".");
        if (parts[0] !== "localhost" && parts[0] !== "127" && parts[0] !== "www") {
          return parts[0];
        }
      }
    }
    return "nextlook";
  }, [subdomain]);

  useEffect(() => {
    if (initialBranches && initialBranches.length > 0) {
      setBranches(initialBranches);
      setLoading(false);
      return;
    }

    async function fetchBranches() {
      setLoading(true);
      const currentSub = getSubdomain();
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${currentSub}.localhost:8000` : "";

        // Branch API Fetch
        const res = await fetch(
          `${apiBase}/api/v1/stores/branches/?subdomain=${encodeURIComponent(currentSub)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Subdomain": currentSub,
            },
          }
        );

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.branches || [];
          setBranches(list);
        }
      } catch (err) {
        console.error("Error fetching branch stores:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchBranches();
  }, [initialBranches, getSubdomain]);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const formatImg = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  // 🌟 ব্রাঞ্চের সঠিক URL তৈরি (১ নম্বর ছবির মতো /b/[branchSlug]?view=store রুটে ওপেন হবে)
  const getBranchUrl = (branch: BranchItem) => {
    const branchSlug = branch.slug || String(branch.id);

    // ব্রাঞ্চের জন্য যদি আলাদা এক্সটার্নাল কাস্টম ডোমেন থাকে (যেমন www.mybranch.com)
    if (
      branch.domain &&
      branch.domain.trim() &&
      !branch.domain.includes("xylotechsolution.com") &&
      !branch.domain.includes("localhost")
    ) {
      return branch.domain.startsWith("http")
        ? branch.domain
        : `https://${branch.domain}`;
    }

    // 🌟 সঠিক সমাধান: একই স্টোরের ভেতরে ব্রাঞ্চ পেজের লিঙ্ক (/b/[slug]?view=store)
    return `/b/${branchSlug}?view=store`;
  };

  if (!loading && branches.length === 0) {
    return null;
  }

  return (
    <section className="w-full py-8 sm:py-12 bg-white select-none font-sans border-t border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
        
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b border-slate-200/80 pb-4">
          <div className="space-y-1 text-left">
            <div className="flex items-center gap-2">
              <span 
                style={{ color: primaryColor }}
                className="text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-1"
              >
                <Building2 className="w-3.5 h-3.5" />
                <span>Our Branch Stores & Physical Outlets</span>
              </span>
              <span className="text-[9px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300">
                Official Branches
              </span>
            </div>

            <h2 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Explore Store Branches & Locations
            </h2>
            <p className="text-xs text-slate-500">
              আপনার নিকটবর্তী ব্রাঞ্চ স্টোর খুঁজে নিন এবং ভিজিট করে এক্সক্লুসিভ লোকাল স্টক দেখে নিন।
            </p>
          </div>

          <div className="flex items-center gap-3 self-start sm:self-auto">
            {branches.length > 3 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleScroll("left")}
                  className="w-8 h-8 rounded-full border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                  aria-label="Previous Branches"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleScroll("right")}
                  className="w-8 h-8 rounded-full border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                  aria-label="Next Branches"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => window.open("/products", "_blank")}
              className="text-xs font-black text-slate-700 hover:text-blue-600 flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
            >
              <span>All Products</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">
              ব্রাঞ্চ স্টোর তালিকা লোড হচ্ছে...
            </span>
          </div>
        ) : (
          /* 🌟 Horizontal Scroll Row Container */
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-none snap-x snap-mandatory py-2 px-1"
          >
            {branches.map((branch, index) => {
              const theme = BRANCH_CARD_THEMES[index % BRANCH_CARD_THEMES.length];
              const branchImg = formatImg(
                branch.banner_url || branch.banner || branch.logo_url || branch.logo || branch.image
              );
              const branchPhone = branch.phone || branch.contact_phone;
              const pCount = branch.total_products ?? branch.products_count ?? 0;
              const visitUrl = getBranchUrl(branch);

              return (
                <div
                  key={branch.id}
                  className="group relative h-[420px] w-[295px] sm:w-[320px] snap-start rounded-3xl bg-white overflow-hidden shadow-md hover:shadow-2xl border border-slate-200/90 flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 select-none shrink-0"
                >
                  {/* 🌟 1. Full Image Cover White Part */}
                  <div className="relative w-full h-[235px] bg-slate-100 overflow-hidden flex flex-col justify-between p-3.5 z-10 border-b border-slate-100">
                    <div className="absolute inset-0 w-full h-full bg-slate-100">
                      {branchImg ? (
                        <img
                          src={branchImg}
                          alt={branch.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-100">
                          <Building2 className="w-16 h-16 text-slate-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60 pointer-events-none" />
                    </div>

                    {/* Top Branch Name Box */}
                    <div className="relative z-10 border border-white/80 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-xl shadow-md w-full flex flex-col items-center justify-center">
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-blue-600 block mb-0.5 truncate max-w-full">
                        {theme.tagText}
                      </span>
                      <h4 className="text-base font-black text-slate-950 uppercase tracking-tight leading-tight line-clamp-1">
                        {branch.name}
                      </h4>
                    </div>

                    {/* Bottom Location & Phone Strip */}
                    <div className="relative z-10 space-y-1">
                      <div className="bg-white/95 backdrop-blur-md px-2.5 py-1 rounded-lg border border-slate-200/80 shadow-xs flex items-center gap-1.5 text-[10px] font-bold text-slate-700 truncate">
                        <MapPin className="w-3 h-3 text-rose-500 shrink-0" />
                        <span className="truncate">{branch.address || "Main City Outlet"}</span>
                      </div>

                      {branchPhone && (
                        <div className="bg-white/95 backdrop-blur-md px-2.5 py-0.5 rounded-lg border border-slate-200/80 shadow-xs flex items-center gap-1.5 text-[10px] font-mono font-bold text-slate-700">
                          <Phone className="w-3 h-3 text-blue-500 shrink-0" />
                          <span>{branchPhone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 🌟 2. Fluid Gradient Wave Section */}
                  <div
                    className={`relative w-full h-44 bg-gradient-to-br ${theme.gradient} flex flex-col items-center justify-end pb-5 px-4 overflow-hidden`}
                  >
                    <svg
                      className="absolute -top-1 left-0 w-full h-16 text-white preserve-3d"
                      viewBox="0 0 500 150"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0,0 C150,90 350,-40 500,60 L500,0 L0,0 Z"
                        fill="white"
                      />
                    </svg>

                    <div
                      className="absolute inset-0 opacity-25"
                      style={{
                        background: `radial-gradient(circle at 50% 120%, ${theme.waveFill}, transparent 70%)`,
                      }}
                    />

                    <div className="relative z-10 text-center mb-2.5">
                      <span className="text-2xl font-black text-white tracking-tight drop-shadow-md block leading-none">
                        {theme.badge}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/90 flex items-center justify-center gap-1 mt-1">
                        <PackageCheck className="w-3 h-3" />
                        <span>{pCount} PRODUCTS AVAILABLE</span>
                      </span>
                    </div>

                    {/* 🌟 3. VISIT NOW (নতুন ট্যাবে সরাসরি /b/[slug]?view=store পেজে যাবে) */}
                    <div className="relative z-10 w-full">
                      <a
                        href={visitUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full py-2 rounded-full border ${theme.btnBorder} bg-white/20 hover:bg-white/35 backdrop-blur-xs flex items-center justify-center gap-1.5 text-[11px] font-black tracking-widest text-white uppercase shadow-sm transition-transform group-hover:scale-102 cursor-pointer`}
                      >
                        <span>VISIT NOW</span>
                        <ExternalLink className="w-3.5 h-3.5 stroke-[2.5]" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}